import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTableCell } from '@/components/shared/SortableTableCell';

export interface PriceChange {
  catalogId: string;
  name: string;
  nsn: string;
  currentPrice: number;
  proposedPrice: number;
  sampleCount: number;
}

export interface PriceUpdateConfirmationModalProps {
  open: boolean;
  priceChanges: PriceChange[];
  onAccept: () => void;
  onSkip: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const formatPrice = (price: number) =>
  price.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const PriceUpdateConfirmationModal = ({
  open,
  priceChanges,
  onAccept,
  onSkip,
  onCancel,
  isSubmitting,
}: PriceUpdateConfirmationModalProps) => {
  const { sortField, sortDirection, onSort, sortedRows } = useTableSort(
    priceChanges as unknown as Record<string, unknown>[],
  );
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      aria-labelledby="price-update-confirmation-title"
      aria-describedby="price-update-confirmation-description"
    >
      <DialogTitle id="price-update-confirmation-title">Update Catalog Prices?</DialogTitle>
      <DialogContent>
        <DialogContentText id="price-update-confirmation-description" sx={{ mb: 2 }}>
          The following item prices differ from their current catalog prices. The proposed prices
          are calculated as a rolling 365-day average from previous requisitions. Would you like to
          update the catalog prices before creating the requisition?
        </DialogContentText>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTableCell field="name" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Item Name</SortableTableCell>
              <SortableTableCell field="nsn" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>NSN</SortableTableCell>
              <SortableTableCell field="currentPrice" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right">Current Price</SortableTableCell>
              <SortableTableCell field="proposedPrice" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right">Proposed Price</SortableTableCell>
              <SortableTableCell field="sampleCount" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right"># Samples</SortableTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(sortedRows as unknown as PriceChange[]).map((change) => (
              <TableRow key={change.catalogId}>
                <TableCell>{change.name}</TableCell>
                <TableCell>{change.nsn}</TableCell>
                <TableCell align="right">{formatPrice(change.currentPrice)}</TableCell>
                <TableCell align="right">{formatPrice(change.proposedPrice)}</TableCell>
                <TableCell align="right">{change.sampleCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={onSkip} variant="outlined" disabled={isSubmitting}>
          Skip &amp; Create
        </Button>
        <Button onClick={onAccept} variant="contained" disabled={isSubmitting} autoFocus>
          {isSubmitting ? 'Creating...' : 'Accept & Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
